
import React, { useState, useEffect, useRef } from "react";
import Topbar from "../top-bar/top-bar";
import { instance } from "../../utils/utils";
import PlanCustomizationAndBilling from "./components/plan-customization-and-billing";
import SubscriptionSummaryAndPayment from "./components/subscription-summary-and-payment";
import "./styles.css";
import { useNavigate } from "react-router-dom";
import { BackIcon } from "./modals/icons/icons";
import { MODAL_CONFIGS } from "./modals/modal-configs";
import InlineModal from "../info-modal/infoModal";
import { getBackendError } from "../../utils/error-helpers";

export default function SubscriptionEdit() {
    const navigate = useNavigate();
    const [planData, setPlanData] = useState(null);
    const [availablePlans, setAvailablePlans] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loading, setLoading] = useState(true);

    const [selectedPlanName, setSelectedPlanName] = useState("");
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
    const [billingPeriod, setBillingPeriod] = useState("monthly");
    const [showInvoice, setShowInvoice] = useState(false);

    const [extraAthletes, setExtraAthletes] = useState(0);
    const [extraAdmins, setExtraAdmins] = useState(0);

    const [invoiceData, setInvoiceData] = useState({
        nit: "",
        fiscalRegime: null,
        fiscalResponsibility: null,
        invoiceEmail: ""
    });

    const [documentType, setDocumentType] = useState("Cédula de Ciudadanía");
    const [quoteData, setQuoteData] = useState(null);
    const [summaryData, setSummaryData] = useState(null);
    const [quoteLoading, setQuoteLoading] = useState(false);
    const [newCardData, setNewCardData] = useState({
        holder_name: "",
        document_type: "Cédula de Ciudadanía",
        document_number: "",
        card_number: "",
        expiration: "",
        cvv: "",
        save_card: false
    });
    const [errors, setErrors] = useState({});
    const [isBlockedByPendingChanges, setIsBlockedByPendingChanges] = useState(false);
    const [pendingChangeMessage, setPendingChangeMessage] = useState("");
    const [activeModal, setActiveModal] = useState(null);
    const closeModal = () => setActiveModal(null);
    const [modalContext, setModalContext] = useState({
        summary: null
    });

    const [infoModal, setInfoModal] = useState(null);
    const [scheduledChangeData, setScheduledChangeData] = useState(null);

    const validateCardData = () => {
        const newErrors = {};
        if (!newCardData.holder_name) newErrors.holder_name = "El nombre es obligatorio";
        if (!newCardData.document_number) newErrors.document_number = "El documento es obligatorio";

        if (!newCardData.card_number) {
            newErrors.card_number = "El número de tarjeta es obligatorio";
        } else if (newCardData.card_number.replace(/\s/g, "").length < 13) {
            newErrors.card_number = "Número de tarjeta inválido";
        }

        if (!newCardData.expiration) {
            newErrors.expiration = "La fecha es obligatoria";
        } else if (!/^\d{2}\/\d{2}$/.test(newCardData.expiration)) {
            newErrors.expiration = "Formato inválido (MM/AA)";
        }

        if (!newCardData.cvv) {
            newErrors.cvv = "El CVV es obligatorio";
        } else if (newCardData.cvv.length < 3 || newCardData.cvv.length > 4) {
            newErrors.cvv = "CVV inválido";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const [initialSnapshot, setInitialSnapshot] = useState(null);

    const debounceRef = useRef(null);

    const fetchAllData = async () => {
        setLoading(true);
        try {
            const [planRes, plansRes, methodsRes] = await Promise.all([
                instance.get("/customers/company/plan/detail"),
                instance.get("/customers/company/plans"),
                instance.get("/customers/company/payment-method/list")
            ]);

            const plan = planRes.data;

            if (plan?.is_reactivable) {
                navigate("/admin/edit-member/renovate", { state: { planData: plan, action: "renovate" } });
                return;
            }

            setPlanData(plan);
            setSelectedPlanName(plan.plan_title);
            setExtraAdmins(plan.extra_admin_users || 0);
            setExtraAthletes(plan.extra_client_slots || 0);

            const isYearly = plan.current_payment_form?.toLowerCase().includes("anual");
            const period = isYearly ? "yearly" : "monthly";
            setBillingPeriod(period);

            setAvailablePlans(plansRes.data);

            const methods = methodsRes.data.payment_methods;
            setPaymentMethods(methods);
            const defaultMethod = methods.find(pm => pm.is_default);
            const defaultMethodId = defaultMethod ? defaultMethod.id : null;
            if (defaultMethodId) {
                setSelectedPaymentMethod(defaultMethodId);
            }

            setInitialSnapshot({
                plan_id: plan.plan_id,
                plan_title: plan.plan_title,
                billingPeriod: period,
                extraAthletes: plan.extra_client_slots || 0,
                extraAdmins: plan.extra_admin_users || 0,
                paymentMethod: defaultMethodId,
                initialIncludedAthletes: plan.contracted_client_slots,
                initialIncludedAdmins: plan.contracted_admin_users,
                initialTotalAthletes:
                    plan.contracted_client_slots,
                initialTotalAdmins:
                    plan.contracted_admin_users
            });


        } catch (error) {
            console.error("Error fetching initial data:", error);
        } finally {
            setLoading(false);
        }
    };

    const money = (value) => {
        if (value == null) return "COP $0";
        const n = Number(value);
        if (Number.isNaN(n)) return "COP $0";
        return `COP $${n.toLocaleString("es-CO")}`;
    };

    const formatDate = (iso) => {
        if (!iso) return "-";
        try {
            const d = new Date(iso);
            const opts = { day: "2-digit", month: "short", year: "numeric" };
            return d.toLocaleDateString("es-CO", opts);
        } catch (e) {
            return iso;
        }
    };

    const getSelectedPlanObj = () => {
        if (!availablePlans.length || !selectedPlanName) return null;
        return availablePlans.find(plan => plan.name === selectedPlanName) || null;
    };

    const getIncludedCountsForSelectedPlan = () => {
        const selected = getSelectedPlanObj();
        if (selected) {
            return {
                included_clients: selected.clients_limit ?? planData?.contracted_client_slots ?? 0,
                included_admins: selected.role_user_limit ?? planData?.contracted_admin_users ?? 0,
                extra_client_price: Number(selected.price_extra_clients || selected.price_extra_clients === 0 ? selected.price_extra_clients : 0),
                extra_admin_price: Number(selected.price_extra_admins || selected.price_extra_admins === 0 ? selected.price_extra_admins : 0),
                base_price: Number(selected.base_price || 0)
            };
        }
        return {
            included_clients: (planData?.contracted_client_slots || 0) - (planData?.extra_client_slots || 0),
            included_admins: (planData?.contracted_admin_users || 0) - (planData?.extra_admin_users || 0),
            extra_client_price: planData?.price_per_user ?? 0,
            extra_admin_price: planData?.price_per_admin ?? 0,
            base_price: planData?.cost ?? 0
        };
    };

    const selectedPlanObj = getSelectedPlanObj();
    const selectedPlanIncluded = getIncludedCountsForSelectedPlan();

    const handleSelectPlan = (planName) => {
        const newPlan = availablePlans.find(p => p.name === planName);
        if (!newPlan) return;

        const targetTotalAthletes = totalAthletes;
        const targetTotalAdmins = totalAdmins;

        const newIncludedClients = newPlan.clients_limit ?? planData?.contracted_client_slots ?? 0;
        const newIncludedAdmins = newPlan.role_user_limit ?? planData?.contracted_admin_users ?? 0;

        const newExtraAthletes = Math.max(0, targetTotalAthletes - newIncludedClients);
        const newExtraAdmins = Math.max(0, targetTotalAdmins - newIncludedAdmins);

        setExtraAthletes(newExtraAthletes);
        setExtraAdmins(newExtraAdmins);
        setSelectedPlanName(planName);
    };

    const buildPlanPayload = (
        selectedPlanObj,
        extraAthletes,
        extraAdmins,
        billingPeriod,
        selectedPaymentMethod,
        {
            showInvoice = false,
            invoiceData = {}
        } = {},
        dry_run = true
    ) => {
        const backendTotalClients = planData.contracted_client_slots || 0;
        const backendTotalAdmins = planData.contracted_admin_users || 0;

        const newBaseClients = selectedPlanObj?.clients_limit ?? (backendTotalClients - (planData.extra_client_slots || 0));
        const newBaseAdmins = selectedPlanObj?.role_user_limit ?? (backendTotalAdmins - (planData.extra_admin_users || 0));

        const minClients = newBaseClients;
        const minAdmins = newBaseAdmins;

        const currentTotalAthletes = minClients + extraAthletes;
        const currentTotalAdmins = minAdmins + extraAdmins;

        const deltaClients = currentTotalAthletes - Math.max(backendTotalClients, newBaseClients);
        const deltaAdmins = currentTotalAdmins - Math.max(backendTotalAdmins, newBaseAdmins);

        return {
            plan_id: selectedPlanObj.id,
            cycle: billingPeriod === "yearly" ? "annual" : "monthly",
            payment_method_id: selectedPaymentMethod === "new" ? null : selectedPaymentMethod,
            total_clients: deltaClients,
            total_role_user_admins: deltaAdmins,

            ...(showInvoice && {
                want_electronic_invoice: true,
                nit_id_number: invoiceData.nit || null,
                tax_regime: invoiceData.fiscalRegime || null,
                tax_responsibility: invoiceData.fiscalResponsibility || null,
                billing_email: invoiceData.invoiceEmail || null,
            }),

            dry_run,
        };
    };

    function buildSummaryFromScheduled(scheduled) {
        if (!scheduled) return null;

        const money = (v) =>
            typeof v === "number"
                ? v.toLocaleString("es-CO", { style: "currency", currency: "COP" })
                : v ?? "-";

        const formatDate = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "-");

        const cycleLabel =
            scheduled.payment_frequency_time === 10 ? "Mensual" :
                scheduled.payment_frequency_time === 20 ? "Anual" :
                    "-";

        const hasExtraClients = scheduled.extra_clients !== 0;
        const hasExtraAdmins = scheduled.extra_roles !== 0;
        const hasUserChanges = hasExtraClients || hasExtraAdmins;

        const summary = {
            is_scheduled: true,
            change_summary: {
                new_plan: scheduled.new_plan_name,
                calculation_detail: {
                    coverage: {
                        change_start: formatDate(scheduled.change_start),
                        new_renewal: formatDate(scheduled.renewal_date)
                    },
                    calculation_data: {
                        new_price_per_user: money(scheduled.cost_per_day_clients ?? 0),
                        users_count: scheduled.total_clients ?? "-",
                        admins_count: scheduled.total_roles ?? "-",
                        paid_amount: money(scheduled.charged_now ?? 0),
                        plan_total_days: scheduled.period_total_days ?? "-"
                    },
                    new_plan_info: {
                        new_cycle: cycleLabel,
                        new_plan: scheduled.new_plan_name || "-",
                        request_date: formatDate(scheduled.change_start),
                        remaining_days: scheduled.period_remaining_days ?? "-",
                        desired_change_value: money(scheduled.new_plan_cost ?? 0)
                    },
                    extra_users_info: hasUserChanges
                        ? {
                            extra_clients: scheduled.extra_clients ?? 0,
                            extra_admins: scheduled.extra_roles ?? 0,
                            remaining_days: scheduled.period_remaining_days ?? "-",
                            desired_change_value: money(scheduled.new_plan_cost ?? 0),
                            new_cycle: cycleLabel
                        }
                        : null
                },
                credit_value: money(scheduled.credit ?? 0),
                total_to_pay_today: money(0)
            }
        };

        return summary;
    }

    const handlePayment = async () => {
        setLoading(true);
        try {
            let paymentMethodId = selectedPaymentMethod;

            if (selectedPaymentMethod === "new") {
                if (!validateCardData()) {
                    setLoading(false);
                    return;
                }

                const cardPayload = {
                    ...newCardData,
                    number: newCardData.card_number.replace(/\s/g, ""),
                    type: "credit_card"
                };

                const pmResponse = await instance.post("/customers/company/payment-method/create", cardPayload);
                paymentMethodId = pmResponse.data.id;
            }

            const selectedPlanObj = getSelectedPlanObj();
            if (!selectedPlanObj) return;

            const payload = buildPlanPayload(
                selectedPlanObj,
                extraAthletes,
                extraAdmins,
                billingPeriod,
                paymentMethodId,
                {
                    showInvoice,
                    invoiceData
                },
                false
            );

            const response = await instance.post("/customers/company/plan/update", payload);
            const data = response.data;

            setModalContext({
                summary: {
                    planName: selectedPlanName,
                    transactionId: data.proration_detail?.order_id || "-",
                    totalPaid: money(data.proration?.charged_now || 0),
                    amountToPay: money(data.proration?.charged_now || 0),
                }
            });

            setActiveModal("SUCCESS");

        } catch (error) {
            console.error("Error processing payment:", error);

            const backendError = error?.response?.data;

            if (backendError?.error_payment) {
                setModalContext({
                    summary: {}
                });
                setActiveModal("PAYMENT_REJECTED_UPDATE");
                return;
            }

            const errorMessage = getBackendError(error);
            setInfoModal({
                iconType: "error",
                title: "Error al actualizar la suscripción",
                description: errorMessage,
                secondaryAction: {
                    label: "Cerrar",
                    onClick: () => setInfoModal(null),
                },
            });
        }
        finally {
            setLoading(false);
        }
    };

    const fetchQuote = async () => {
        if (!planData || !selectedPlanName) return;

        const selectedPlanObj = getSelectedPlanObj();
        if (!selectedPlanObj) return;

        const payload = buildPlanPayload(
            selectedPlanObj,
            extraAthletes,
            extraAdmins,
            billingPeriod,
            selectedPaymentMethod
        );

        try {
            setQuoteLoading(true);

            const response = await instance.post("/customers/company/plan/update", payload);
            setQuoteData(response.data);

            const built = buildSummaryFromApi(response.data, selectedPlanObj);
            setSummaryData(built);

        } catch (error) {
            console.error("Error fetching quote:", error);

            const backendMsg = error?.response?.data?.error;
            if (backendMsg?.toLowerCase().includes("tienes cambios pendientes")) {
                setIsBlockedByPendingChanges(true);
                setPendingChangeMessage(backendMsg);
                setQuoteData(null);
                setSummaryData(null);

                try {
                    const scheduledResponse = await instance.get("/customers/company/plan/scheduled/detail");
                    const normalized = buildSummaryFromScheduled(scheduledResponse.data);
                    setSummaryData(normalized);
                } catch (schedError) {
                    console.error("Error fetching scheduled plan details:", schedError);
                    setSummaryData(null);
                }
                return;
            }

            setQuoteData(null);
            setSummaryData(null);
        } finally {
            setQuoteLoading(false);
        }
    };

    const handleIncrementAthletes = () => {
        setExtraAthletes(prev => prev + 10);
    };

    const handleDecrementAthletes = () => {
        setExtraAthletes(prev => Math.max(0, prev - 10));
    };

    const handleIncrementAdmins = () => {
        setExtraAdmins(prev => prev + 1);
    };

    const handleDecrementAdmins = () => {
        setExtraAdmins(prev => Math.max(0, prev - 1));
    };

    const minAdmins = selectedPlanIncluded.included_admins;

    const minAthletes = selectedPlanIncluded.included_clients;

    const totalAdmins = minAdmins + extraAdmins;
    const totalAthletes = minAthletes + extraAthletes;

    const handleInvoiceDataChange = (field, value) => {
        setInvoiceData(prev => ({ ...prev, [field]: value }));
    };

    const getBasePrice = () => {
        if (!planData) return 0;
        const basePrice = planData?.base_price || planData?.cost || 0;
        return billingPeriod === "yearly" ? basePrice * 12 * 0.8 : basePrice;
    };

    const getExtraUsersPrice = () => {
        if (!planData) return 0;
        const pricePerUser = planData?.price_per_user || 0;
        const extraCost = extraAthletes * pricePerUser;
        return billingPeriod === "yearly" ? extraCost * 12 * 0.8 : extraCost;
    };

    const getExtraAdminsPrice = () => {
        if (!planData) return 0;
        const pricePerAdmin = planData?.price_per_admin || 0;
        const extraCost = extraAdmins * pricePerAdmin;
        return billingPeriod === "yearly" ? extraCost * 12 * 0.8 : extraCost;
    };

    const getAthletesDiff = () => {
        if (!planData) return 0;
        const backendTotalClients = planData.contracted_client_slots || 0;
        const selectedPlanObj = getSelectedPlanObj();
        const newBaseClients = selectedPlanObj?.clients_limit ?? (backendTotalClients - (planData.extra_client_slots || 0));

        return totalAthletes - Math.max(backendTotalClients, newBaseClients);
    };

    const getAdminsDiff = () => {
        if (!planData) return 0;
        const backendTotalAdmins = planData.contracted_admin_users || 0;
        const selectedPlanObj = getSelectedPlanObj();
        const newBaseAdmins = selectedPlanObj?.role_user_limit ?? (backendTotalAdmins - (planData.extra_admin_users || 0));

        return totalAdmins - Math.max(backendTotalAdmins, newBaseAdmins);
    };

    const getTotalPrice = () => {
        return getBasePrice() + getExtraUsersPrice() + getExtraAdminsPrice();
    };

    const hasFormChanges = () => {
        if (!initialSnapshot || !planData) return false;
        const selectedPlanObj = getSelectedPlanObj();
        const planChanged = selectedPlanObj ? (selectedPlanObj.id !== initialSnapshot.plan_id) : false;
        const billingChanged = initialSnapshot.billingPeriod !== billingPeriod;
        const athletesChanged = (initialSnapshot.extraAthletes || 0) !== extraAthletes;
        const adminsChanged = (initialSnapshot.extraAdmins || 0) !== extraAdmins;
        const paymentChanged = (initialSnapshot.paymentMethod || null) !== (selectedPaymentMethod || null);

        return planChanged || billingChanged || athletesChanged || adminsChanged || paymentChanged;
    };

    function buildSummaryFromApi(api, planData = null) {
        if (!api) return null;

        const detail = api.proration_detail || {};
        const proration = api.proration || {};
        const limits = api.limits || {};
        const flags = api.flags || {};

        // ---------------------------------------
        // HELPERS
        // ---------------------------------------
        const money = (v) =>
            typeof v === "number"
                ? v.toLocaleString("es-CO", { style: "currency", currency: "COP" })
                : v ?? "-";

        const formatDate = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "-");

        // ---------------------------------------
        // CYCLE CALCULATION
        // ---------------------------------------
        let currentCycle = initialSnapshot?.billingPeriod === "yearly" ? 20 : 10
        let newCycle = detail.payment_frequency_time;
        const cycleChanged = currentCycle !== newCycle;


        const newCycleLabel =
            newCycle === 10 ? "Mensual" :
                newCycle === 20 ? "Anual" :
                    "-";

        // ---------------------------------------
        // PLAN CHANGE DETECTION
        // ---------------------------------------
        const planChanged = api.is_plan_change === true;

        // ---------------------------------------
        // USER/ADMIN CHANGES
        // ---------------------------------------
        const hasExtraClients = limits?.clients?.diff != 0;
        const hasExtraAdmins = limits?.admins?.diff != 0;
        const hasUserChanges = hasExtraClients || hasExtraAdmins;

        // ---------------------------------------
        // NEW PLAN NAME
        // ---------------------------------------
        const newPlanName =
            api.plan?.title ??
            api.plan?.name ??
            "Nuevo plan";

        // ---------------------------------------
        // BASE STRUCTURE
        // ---------------------------------------
        const summary = {
            change_summary: {
                calculation_detail: {
                    coverage: {
                        change_start: formatDate(detail.change_start),
                        new_renewal: formatDate(api.renewal_date)
                    },
                    calculation_data: {
                        new_price_per_user: money(api.plan?.price ?? 0),
                        users_count: limits?.clients?.new ?? "-",
                        admins_count: limits?.admins?.new ?? "-",
                        paid_amount: money(proration?.charged_now ?? 0),
                        plan_total_days: detail.period_total_days ?? "-",
                    },
                    new_plan_info: null,
                    extra_users_info: null
                },
                credit_value: money(
                    proration?.credit ??
                    api.proration_detail?.credit ??
                    0
                ),
                total_to_pay_today: money(proration?.charged_now ?? 0)
            }
        };

        // --- NEW PLAN INFO ---
        if (planChanged || cycleChanged) {
            summary.change_summary.calculation_detail.new_plan_info = {
                new_cycle: newCycleLabel,
                new_plan: newPlanName,
                request_date: formatDate(detail.change_start),
                remaining_days: detail.period_remaining_days ?? "-",
                desired_change_value: money(
                    detail.new_plan_cost ??
                    proration.new_period_cost ??
                    proration.future_amount_to_charge ??
                    0
                )
            };
        }

        // --- EXTRA USERS INFO ---
        if (hasUserChanges) {
            summary.change_summary.calculation_detail.extra_users_info = {
                extra_clients: detail.extra_clients ?? 0,
                extra_admins: detail.extra_roles ?? 0,
                remaining_days: detail.period_remaining_days ?? "-",
                desired_change_value: money(
                    detail.new_plan_cost ??
                    proration.new_period_cost ??
                    proration.future_amount_to_charge ??
                    0
                )
            };
        }

        return summary;
    }

    const handleCancelSubscription = async () => {
        setLoading(true);
        try {
            await instance.post("/customers/company/plan/cancel/trial");
        } catch (error) {
            console.error("Error al cancelar suscripción:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSkipTrial = async () => {
        setLoading(true);
        try {
            await instance.post("/customers/company/plan/skip-trial");
            fetchAllData();
            fetchQuote();
            setActiveModal(null);
            setModalContext({});
        } catch (error) {
            console.error("Error al saltar la prueba gratuita:", error);

            const backendError = error?.response?.data;

            if (backendError?.error_payment) {
                setModalContext({
                    summary: {}
                });
                setActiveModal("PAYMENT_REJECTED_UPDATE");
                return;
            }

        } finally {
            setLoading(false);
        }
    };

    const handleRevertPendingChange = async () => {
        setLoading(true);
        try {
            await instance.post("/customers/company/plan/scheduled/cancel");
            fetchAllData();
            fetchQuote();
            setIsBlockedByPendingChanges(false);
        } catch (error) {
            console.error("Error al revertir el cambio pendiente:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAllData();
    }, []);

    useEffect(() => {
        if (!planData) return;

        if (planData.is_trial) {
            setModalContext({
                summary: {
                    amountToPay: money(planData.cost || 0)
                }
            });
            setActiveModal("CONFIRM_PAYMENT");
        } else {
            setActiveModal(null);
        }
    }, [planData]);

    useEffect(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
            fetchQuote();
        }, 1000);

        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [selectedPlanName, billingPeriod, selectedPaymentMethod, extraAthletes, extraAdmins]);

    return (
        <>
            <div className="w-100">
                <Topbar />
                <div className="subscription-edit-container w-100 p-3 px-5 gap-4 gap-xxl-5 d-flex flex-column">
                    <div className="subscription-edit-header d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-start gap-2">
                            <div className="subscription-edit-title mb-0 d-flex flex-row align-items-start gap-2 cursor-pointer" onClick={() => navigate("/admin/")}>
                                <BackIcon />
                                <div className="subscription-edit-title d-flex flex-column">
                                    <span>Gestión de Suscripción</span>
                                    <small>--</small>
                                </div>
                            </div>
                        </div>
                    </div>

                    {isBlockedByPendingChanges && (
                        <div className="subscription-edit-pending-changes d-flex flex-row gap-1 p-3">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="9" stroke="white" stroke-width="2" />
                                <path d="M16.5 12H12.25C12.1119 12 12 11.8881 12 11.75V8.5" stroke="white" stroke-width="2" stroke-linecap="round" />
                            </svg>
                            <div className="d-flex flex-column align-items-start gap-2">
                                <span className="subscription-edit-pending-changes-title">Tienes cambios pendientes</span>
                                <div className="subscription-edit-pending-changes-description">
                                    {pendingChangeMessage}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="row gx-4 h-100">
                        <PlanCustomizationAndBilling
                            planData={planData}
                            selectedPlanName={selectedPlanName}
                            handleSelectPlan={handleSelectPlan}
                            availablePlans={availablePlans}
                            billingPeriod={billingPeriod}
                            setBillingPeriod={setBillingPeriod}
                            totalAdmins={totalAdmins}
                            totalAthletes={totalAthletes}
                            handleDecrementAdmins={handleDecrementAdmins}
                            handleIncrementAdmins={handleIncrementAdmins}
                            handleDecrementAthletes={handleDecrementAthletes}
                            handleIncrementAthletes={handleIncrementAthletes}
                            getAdminsDiff={getAdminsDiff}
                            getAthletesDiff={getAthletesDiff}
                            selectedPlanIncluded={selectedPlanIncluded}
                            showInvoice={showInvoice}
                            setShowInvoice={setShowInvoice}
                            invoiceData={invoiceData}
                            handleInvoiceDataChange={handleInvoiceDataChange}
                            isBlockedByPendingChanges={isBlockedByPendingChanges}
                            loading={loading}
                        />

                        <SubscriptionSummaryAndPayment
                            planData={planData}
                            quoteLoading={quoteLoading}
                            hasFormChanges={hasFormChanges}
                            summaryData={summaryData}
                            money={money}
                            formatDate={formatDate}
                            selectedPlanName={selectedPlanName}
                            getTotalPrice={getTotalPrice}
                            getSelectedPlanObj={getSelectedPlanObj}
                            handlePayment={handlePayment}
                            loading={loading}
                            paymentMethods={paymentMethods}
                            selectedPaymentMethod={selectedPaymentMethod}
                            setSelectedPaymentMethod={setSelectedPaymentMethod}
                            newCardData={newCardData}
                            setNewCardData={setNewCardData}
                            errors={errors}
                            validateCardData={validateCardData}
                            isBlockedByPendingChanges={isBlockedByPendingChanges}
                            handleRevertPendingChange={handleRevertPendingChange}
                        />
                    </div>
                </div>
            </div>
            {activeModal && (
                <InlineModal
                    {...MODAL_CONFIGS[activeModal]({
                        navigate,
                        handleSkipTrial,
                        handlePayment,
                        cancelSubscription: handleCancelSubscription,
                        openCancelModal: () => setActiveModal("CANCEL_WARNING"),
                        closeModal,
                        ...modalContext
                    })}
                />
            )}
            {infoModal && (
                <InlineModal
                    iconType={infoModal.iconType}
                    title={infoModal.title}
                    description={infoModal.description}
                    primaryAction={infoModal.primaryAction}
                    secondaryAction={infoModal.secondaryAction}
                />
            )}
        </>
    );
}
